import {
    ComponentHeader,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Citation,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Citation";

const USAGE_CODE = `<Citation
    sources={[
        {
            snippet: "More than half of the budget goes to performance channels.",
            title: "Winter season media planning guide",
            domain: "example.com",
            url: "https://example.com/guide",
            date: new Date(2026, 9, 12)
        }
    ]}
    index={1}
/>

<Citation
    sources={[
        {
            title: "Winter season media planning guide",
            url: "https://example.com/guide"
        },
        {
            title: "Quarterly campaign report",
            url: "https://reports.example.org/q3"
        }
    ]}
    variant="source"
/>

<Citation
    sources={[]}
    isDisabled={true}
    index={3}
/>`;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("citationPage.multipleTitle"),
        id: "multiple"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("citationPage.behaviourTitle"),
        id: "behaviour"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const CitationPage = () => {
    const {
        t
    } = useDocsI18n();

    const getGuide = () => ({
        snippet: t("citationPage.guideSnippet"),
        title: t("citationPage.guideTitle"),
        url: "https://pazarlamaokulu.com",
        domain: "pazarlamaokulu.com",
        date: new Date(2026, 9, 12)
    });

    const getReport = () => ({
        snippet: t("citationPage.reportSnippet"),
        title: t("citationPage.reportTitle"),
        url: "https://rapor.nibgat.com",
        date: new Date(2025, 10, 3),
        domain: "rapor.nibgat.com"
    });

    return <Article>
        <ComponentHeader
            lead={t("citationPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("citationPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("citationPage.overviewCaption")}
            >
                <Text
                    variant="bodyMediumSize"
                >
                    {t("citationPage.sentence")}
                </Text>
                <Citation
                    sources={[
                        getGuide()
                    ]}
                    index={1}
                />
                <Citation
                    sources={[
                        getReport()
                    ]}
                    index={2}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("citationPage.variantsText")}
            </Paragraph>
            <Preview>
                <Citation
                    variant="number"
                    sources={[
                        getGuide()
                    ]}
                    index={1}
                />
                <Citation
                    variant="source"
                    sources={[
                        getGuide()
                    ]}
                />
            </Preview>
        </Section>
        <Section
            title={t("citationPage.multipleTitle")}
            id="multiple"
        >
            <Paragraph>
                {t("citationPage.multipleText")}
            </Paragraph>
            <Preview
                caption={t("citationPage.multipleCaption")}
            >
                <Citation
                    variant="source"
                    sources={[
                        getGuide(),
                        getReport()
                    ]}
                />
                <Citation
                    variant="number"
                    sources={[
                        getGuide(),
                        getReport()
                    ]}
                    index={3}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("citationPage.statesText")}
            </Paragraph>
            <Preview>
                <Citation
                    sources={[
                        getGuide()
                    ]}
                    index={1}
                />
                <Citation
                    isDisabled={true}
                    sources={[
                        getGuide()
                    ]}
                    index={2}
                />
                <Citation
                    index={3}
                />
            </Preview>
        </Section>
        <Section
            title={t("citationPage.behaviourTitle")}
            id="behaviour"
        >
            <BulletList
                items={[
                    t("citationPage.behaviourHover"),
                    t("citationPage.behaviourPress"),
                    t("citationPage.behaviourTouch"),
                    t("citationPage.behaviourOpen"),
                    t("citationPage.behaviourEmpty")
                ]}
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("citationPage.a11yButton"),
                    t("citationPage.a11yKeyboard"),
                    t("citationPage.a11yLabel"),
                    t("citationPage.a11yDisabled"),
                    t("citationPage.a11yFocus")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("citationPage.propSources"),
                        type: "Array<ISource>",
                        defaultValue: "[]",
                        name: "sources"
                    },
                    {
                        description: t("citationPage.propIndex"),
                        type: "number",
                        name: "index"
                    },
                    {
                        description: t("citationPage.propVariant"),
                        type: "\"number\" | \"source\"",
                        defaultValue: "\"number\"",
                        name: "variant"
                    },
                    {
                        description: t("citationPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("citationPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("citationPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("citationPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("citationPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("citationPage.sourceText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("citationPage.propSourceTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("citationPage.propSourceUrl"),
                        type: "string",
                        name: "url"
                    },
                    {
                        description: t("citationPage.propSourceDomain"),
                        type: "string",
                        name: "domain"
                    },
                    {
                        description: t("citationPage.propSourceDate"),
                        type: "Date | number | string",
                        name: "date"
                    },
                    {
                        description: t("citationPage.propSourceSnippet"),
                        name: "snippet",
                        type: "string"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default CitationPage;
